import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { SessionExportModal, ProgressReportExportModal } from '../components/ExportModal';
import SessionForm from '../components/SessionForm';
import MessageThread from '../components/MessageThread';
import LoadingLeaf from '../components/LoadingLeaf';
import {
    getClients, getSessions, updateClient, deleteClient, getTransformationArc, getProfile,
    getMessages, generateAccessCode,
    resetClientPortalPassword, sendClientInvite, generateMagicLink,
    buildAvatar, updateAvatarSection, getClientGroups, markMessagesRead,
    getClientTelegramLink, disconnectClientTelegram,
    getClientGoals, createClientGoal, updateClientGoal, deleteClientGoal,
    checkClientEmail,
    PORTAL_BASE_URL
} from '../api/client';

// Veronica's design (TC offering design logic, p7-8) is still open on whether
// Progress stays a tab. Flip to false to fold Progress's content into the
// bottom of Overview instead.
const SHOW_PROGRESS_TAB = true;

export default function ClientProfile() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [client, setClient] = useState(null);
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('sessions');
    const [selectedSession, setSelectedSession] = useState(null);
    const [showAddSession, setShowAddSession] = useState(false);
    const [editingNotes, setEditingNotes] = useState(false);
    const [notesValue, setNotesValue] = useState('');
    const [emailValue, setEmailValue] = useState('');
    const [sendingInvite, setSendingInvite] = useState(false);
    const [newLongGoal, setNewLongGoal] = useState('');
    const [clientGoals, setClientGoals] = useState([]);
    const [newClientGoalText, setNewClientGoalText] = useState('');
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);
    const [transformationArc, setTransformationArc] = useState(null);
    const [loadingArc, setLoadingArc] = useState(false);
    const [arcGenerated, setArcGenerated] = useState(false);
    const [showSessionExport, setShowSessionExport] = useState(false);
    const [exportingSession, setExportingSession] = useState(null);
    const [showProgressExport, setShowProgressExport] = useState(false);
    const [profile, setProfile] = useState(null);
    const [accessCode, setAccessCode] = useState('');
    const [generatingCode, setGeneratingCode] = useState(false);
    const [resettingPassword, setResettingPassword] = useState(false);
    const [messages, setMessages] = useState([]);
    const [avatar, setAvatar] = useState(null);
    const [buildingAvatar, setBuildingAvatar] = useState(false);
    const [editingAvatarSection, setEditingAvatarSection] = useState(null);
    const [editingAvatarValue, setEditingAvatarValue] = useState('');
    const [savingAvatar, setSavingAvatar] = useState(false);
    const [customCode, setCustomCode] = useState('');
    const [clientGroups, setClientGroups] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [telegramLink, setTelegramLink] = useState('');
    const [telegramConnected, setTelegramConnected] = useState(false);
    const [sendingMagicLink, setSendingMagicLink] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteConfirmText, setDeleteConfirmText] = useState('');
    const [deletingClient, setDeletingClient] = useState(false);
    const [pendingCapture, setPendingCapture] = useState(null);
    const [captureKey, setCaptureKey] = useState(0);
    const [captureDragging, setCaptureDragging] = useState(false);
    const [showFullAvatar, setShowFullAvatar] = useState(false);
    const [showMoreMenu, setShowMoreMenu] = useState(false);
    const moreMenuRef = useRef(null);
    const captureInputRef = useRef(null);

    const [searchParams] = useSearchParams();
    useEffect(() => {
        let tab = searchParams.get('tab');
        // Old links: Settings folded into Client Info; Progress may be hidden.
        if (tab === 'settings') tab = 'clientinfo';
        if (tab === 'progress' && !SHOW_PROGRESS_TAB) tab = 'overview';
        if (tab) setActiveTab(tab);
        if (tab === 'progress' || (tab === 'overview' && !SHOW_PROGRESS_TAB)) fetchClientGoals();
    }, []);

    useEffect(() => {
        if (!showMoreMenu) return;
        const onMouseDown = (e) => { if (moreMenuRef.current && !moreMenuRef.current.contains(e.target)) setShowMoreMenu(false); };
        const onKeyDown = (e) => { if (e.key === 'Escape') setShowMoreMenu(false); };
        document.addEventListener('mousedown', onMouseDown);
        document.addEventListener('keydown', onKeyDown);
        return () => { document.removeEventListener('mousedown', onMouseDown); document.removeEventListener('keydown', onKeyDown); };
    }, [showMoreMenu]);
    useEffect(() => {
        fetchData();
        const interval = setInterval(() => {
            getMessages(id).then(res => {
                const msgs = res.data || [];
                setMessages(msgs);
                if (activeTab !== 'messages') {
                    setUnreadCount(msgs.filter(m => m.sender_type === 'client' && !m.read_at).length);
                }
            });
        }, 10000);
        return () => clearInterval(interval);
    }, [id]);

    const fetchData = async () => {
        // getClients() is the one call this page can't render without — find the
        // client first and bail on failure. The rest (sessions/profile/messages/
        // groups) are secondary: fetched independently below so a hiccup in any one
        // of them doesn't blank the whole profile out from under a client that DID
        // load successfully (same fix as Clients.js/Dashboard.js's Promise.all split).
        try {
            const clientsRes = await getClients();
            const found = clientsRes.data.find(c => c.id === id);
            if (!found) { navigate('/clients'); return; }
            setClient(found);
            setNotesValue(found.notes || '');
            setEmailValue(found.email || '');
            setAccessCode(found.access_code || '');
            setAvatar(found.avatar || null);

            if (found.access_code) {
                try {
                    const tgRes = await getClientTelegramLink(id);
                    setTelegramLink(tgRes.data.link);
                    setTelegramConnected(tgRes.data.telegram_connected);
                } catch (err) {
                    // non-critical — Telegram link section just won't show this load
                }
            }
        } catch (err) {
            setError('Could not load client data.');
            setLoading(false);
            return;
        }
        setLoading(false);

        getSessions(id).then(res => setSessions(res.data)).catch(() => {});
        getProfile().then(res => setProfile(res.data)).catch(() => {});
        getMessages(id).then(res => {
            const msgs = res.data || [];
            setMessages(msgs);
            setUnreadCount(msgs.filter(m => m.sender_type === 'client' && !m.read_at).length);
        }).catch(() => {});
        getClientGroups(id).then(res => setClientGroups(res.data || [])).catch(() => {});
    };

    const handleTabChange = (tabId) => {
        setActiveTab(tabId);
        setSelectedSession(null);
        setShowAddSession(false);
        setPendingCapture(null);
        setShowFullAvatar(false);
        if (tabId === 'messages') {
            markMessagesRead(id);
            setUnreadCount(0);
        }
        if (tabId === 'progress' || (tabId === 'overview' && !SHOW_PROGRESS_TAB)) {
            fetchClientGoals();
        }
    };

    const fetchClientGoals = async () => {
        try {
            const res = await getClientGoals(id);
            setClientGoals(res.data || []);
        } catch {}
    };

    const addNewClientGoal = async () => {
        if (!newClientGoalText.trim()) return;
        try {
            const res = await createClientGoal(id, { text: newClientGoalText.trim(), source: 'coach' });
            setClientGoals(prev => [res.data, ...prev]);
            setNewClientGoalText('');
        } catch {
            setError('Could not add goal.');
        }
    };

    const changeClientGoalStatus = async (goalId, status) => {
        try {
            await updateClientGoal(id, goalId, { status });
            setClientGoals(prev => prev.map(g => g.id === goalId ? { ...g, status } : g));
        } catch {
            setError('Could not update goal.');
        }
    };

    const removeClientGoal = async (goalId) => {
        try {
            await deleteClientGoal(id, goalId);
            setClientGoals(prev => prev.filter(g => g.id !== goalId));
        } catch {
            setError('Could not delete goal.');
        }
    };

    const showToast = (msg) => {
        const el = document.createElement('div');
        el.textContent = msg;
        el.style.cssText = 'position:fixed;bottom:24px;right:24px;background:var(--forest);color:white;padding:12px 20px;border-radius:10px;font-size:13.5px;z-index:9999;font-family:DM Sans,sans-serif;box-shadow:0 4px 16px rgba(0,0,0,0.2)';
        document.body.appendChild(el);
        setTimeout(() => el.remove(), 2500);
    };

    const saveClientUpdate = async (updates) => {
        setSaving(true);
        try {
            await updateClient(id, { ...client, ...updates });
            setClient(prev => ({ ...prev, ...updates }));
        } catch (err) {
            setError('Could not save changes.');
        } finally {
            setSaving(false);
        }
    };

    const addLongGoal = async () => {
        if (!newLongGoal.trim()) return;
        const updated = [...(client.long_goals || []), { text: newLongGoal.trim(), status: 'active', added: new Date().toISOString() }];
        await saveClientUpdate({ long_goals: updated });
        setNewLongGoal('');
    };

    const toggleGoalStatus = async (idx, status) => {
        const updated = (client.long_goals || []).map((g, i) => i === idx ? { ...g, status } : g);
        await saveClientUpdate({ long_goals: updated });
    };

    const removeGoal = async (idx) => {
        const updated = (client.long_goals || []).filter((_, i) => i !== idx);
        await saveClientUpdate({ long_goals: updated });
    };

    const saveEmail = async () => {
        const trimmedEmail = emailValue.trim();
        if (trimmedEmail) {
            try {
                const checkRes = await checkClientEmail(trimmedEmail, id);
                const match = checkRes.data.client;
                if (match && !window.confirm(`You already have a client named "${match.name}" with this email. Use this email anyway?`)) {
                    return;
                }
            } catch (err) {
                // non-critical — if the check itself fails, don't block saving
            }
        }
        await saveClientUpdate({ email: trimmedEmail });
        showToast('Email saved');
    };

    const handleSendInvite = async () => {
        setSendingInvite(true);
        try {
            await sendClientInvite(id);
            showToast('Invite sent');
        } catch (err) {
            setError(err.response?.data?.detail || 'Could not send invite.');
        } finally {
            setSendingInvite(false);
        }
    };

    const handleSendMagicLink = async () => {
        setSendingMagicLink(true);
        try {
            await generateMagicLink(id);
            showToast('Magic link sent');
        } catch (err) {
            setError(err.response?.data?.detail || 'Could not send magic link.');
        } finally {
            setSendingMagicLink(false);
        }
    };

    const saveNotes = async () => {
        await saveClientUpdate({ notes: notesValue });
        setEditingNotes(false);
    };

    const generateTransformationArc = async () => {
        setLoadingArc(true);
        try {
            const res = await getTransformationArc(id);
            setTransformationArc(res.data);
            setArcGenerated(true);
        } catch (err) {
            setError('Could not generate transformation arc.');
        } finally {
            setLoadingArc(false);
        }
    };

    const handleBuildAvatar = async () => {
        setBuildingAvatar(true);
        try {
            const res = await buildAvatar(id);
            setAvatar(res.data.avatar);
            showToast('Avatar built successfully');
        } catch (err) {
            setError('Could not build avatar.');
        } finally {
            setBuildingAvatar(false);
        }
    };

    const handleSaveAvatarSection = async (section) => {
        setSavingAvatar(true);
        try {
            const res = await updateAvatarSection(id, { [section]: editingAvatarValue });
            setAvatar(res.data.avatar);
            setEditingAvatarSection(null);
            setEditingAvatarValue('');
            showToast('Avatar updated');
        } catch (err) {
            setError('Could not save avatar section.');
        } finally {
            setSavingAvatar(false);
        }
    };

    const handleGenerateCode = async () => {
        if (!customCode.trim()) return;
        setGeneratingCode(true);
        try {
            const res = await generateAccessCode(id, customCode.trim());
            setAccessCode(res.data.access_code);
            setClient(prev => ({ ...prev, access_code: res.data.access_code }));
            setCustomCode('');
            showToast('Access code set');
        } catch (err) {
            setError('Could not set access code — it may already be taken.');
        } finally {
            setGeneratingCode(false);
        }
    };

    const handleResetPortalPassword = async () => {
        if (!window.confirm(`Reset ${client.name}'s portal password? They'll be signed out and asked to set a new password next time they use their invite link.`)) return;
        setResettingPassword(true);
        try {
            await resetClientPortalPassword(id);
            showToast('Portal password reset');
        } catch (err) {
            setError('Could not reset portal password.');
        } finally {
            setResettingPassword(false);
        }
    };

    const handleDeleteClient = async () => {
        if (deleteConfirmText !== 'DELETE') return;
        setDeletingClient(true);
        try {
            await deleteClient(id);
            navigate('/clients');
        } catch (err) {
            setError('Could not delete client.');
            setDeletingClient(false);
            setShowDeleteModal(false);
        }
    };

    const handleDisconnectTelegram = async () => {
        if (!window.confirm('Disconnect this client from Telegram?')) return;
        await disconnectClientTelegram(id);
        setTelegramConnected(false);
        showToast('Telegram disconnected');
    };

    // Derived data
    const allBreakthroughs = sessions.flatMap(s =>
        (s.breakthrough_quotes || []).map(bq => ({ ...bq, sessionDate: s.date, sessionTitle: s.title, sessionType: s.session_type }))
    ).sort((a, b) => (b.starred ? 1 : 0) - (a.starred ? 1 : 0));
    const starredBreakthroughs = allBreakthroughs.filter(bq => bq.starred);
    const totalSessions = sessions.length;
    const firstSession = sessions.length > 0 ? sessions[sessions.length - 1]?.date : null;
    const allThemes = sessions.flatMap(s => s.summary?.keyThemes || []);
    const themeCounts = allThemes.reduce((acc, theme) => { acc[theme] = (acc[theme] || 0) + 1; return acc; }, {});
    const topThemes = Object.entries(themeCounts).sort((a, b) => b[1] - a[1]).slice(0, 8);
    const initials = client?.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();

    if (loading) return <LoadingLeaf fullScreen size={72} label="Loading client profile…" />;
    if (!client) return null;


    // Cards shared between tabs (Overview snapshot, Progress, Sessions).
    const arcCard = (
        <div style={styles.card}>
                                <div style={styles.cardHeader}>
                                    <span style={styles.cardTitle}>◈ Transformation arc</span>
                                    {sessions.length >= 2 && (
                                        <button style={styles.cardAction} onClick={generateTransformationArc} disabled={loadingArc}>
                                            {loadingArc ? 'Generating...' : arcGenerated ? '↻ Regenerate' : 'Generate arc'}
                                        </button>
                                    )}
                                </div>
                                <div style={styles.cardBody}>
                                    {sessions.length < 2 && (
                                        <p style={styles.emptyText}>Record at least 2 sessions to generate a transformation arc.</p>
                                    )}
                                    {sessions.length >= 2 && !arcGenerated && !loadingArc && (
                                        <div style={styles.arcPrompt}>
                                            <div style={styles.arcPromptIcon}>◈</div>
                                            <div style={styles.arcPromptText}>
                                                Generate an AI-powered narrative of {client.name}'s journey across all {sessions.length} sessions.
                                            </div>
                                            <button style={styles.arcBtn} onClick={generateTransformationArc}>Generate transformation arc</button>
                                        </div>
                                    )}
                                    {loadingArc && (
                                        <div style={styles.arcLoading}>
                                            <div style={styles.arcLoadingText}>Analyzing {sessions.length} sessions...</div>
                                            <div style={styles.progressBar}><div style={styles.progressFill} /></div>
                                        </div>
                                    )}
                                    {arcGenerated && transformationArc?.arc && (
                                        <div>
                                            <div style={styles.arcHeadline}>{transformationArc.arc.headline}</div>
                                            <div style={styles.arcNarrative}>
                                                {transformationArc.arc.arc?.split('\n\n').map((para, i) => (
                                                    <p key={i} style={styles.arcParagraph}>{para}</p>
                                                ))}
                                            </div>
                                            {transformationArc.arc.coreShifts?.length > 0 && (
                                                <div style={styles.arcSection}>
                                                    <div style={styles.arcSectionTitle}>Core shifts</div>
                                                    {transformationArc.arc.coreShifts.map((shift, i) => (
                                                        <div key={i} style={styles.arcShift}>→ {shift}</div>
                                                    ))}
                                                </div>
                                            )}
                                            {transformationArc.arc.growthEdges?.length > 0 && (
                                                <div style={styles.arcSection}>
                                                    <div style={styles.arcSectionTitle}>Growth edges</div>
                                                    {transformationArc.arc.growthEdges.map((edge, i) => (
                                                        <div key={i} style={styles.arcEdge}>◎ {edge}</div>
                                                    ))}
                                                </div>
                                            )}
                                            {transformationArc.arc.coachingInsights && (
                                                <div style={styles.arcInsights}>
                                                    <div style={styles.arcSectionTitle}>Coach insights</div>
                                                    <div style={styles.arcInsightsText}>{transformationArc.arc.coachingInsights}</div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
    );

    const fullAvatarCard = (
        <div style={styles.card}>
                                <div style={styles.cardHeader}>
                                    <span style={styles.cardTitle}>◈ Client avatar</span>
                                    <button style={styles.cardAction} onClick={handleBuildAvatar} disabled={buildingAvatar}>
                                        {buildingAvatar ? 'Building...' : avatar ? '↻ Rebuild' : 'Build avatar'}
                                    </button>
                                </div>
                                <div style={styles.cardBody}>
                                    {sessions.length === 0 && (
                                        <div style={styles.emptyState}>
                                            <div style={styles.emptyIcon}>◈</div>
                                            <p style={styles.emptyText}>Record at least one session to build their avatar.</p>
                                        </div>
                                    )}
                                    {sessions.length > 0 && !avatar && !buildingAvatar && (
                                        <div style={styles.arcPrompt}>
                                            <div style={styles.arcPromptIcon}>◈</div>
                                            <div style={styles.arcPromptText}>Build a deep coaching avatar for {client.name} — synthesized from all {sessions.length} sessions and messages.</div>
                                            <button style={styles.arcBtn} onClick={handleBuildAvatar}>Build avatar</button>
                                        </div>
                                    )}
                                    {buildingAvatar && (
                                        <div style={styles.arcLoading}>
                                            <div style={styles.arcLoadingText}>Building {client.name}'s avatar...</div>
                                            <div style={styles.progressBar}><div style={styles.progressFill} /></div>
                                        </div>
                                    )}
                                    {avatar && !buildingAvatar && (
                                        <div>
                                            {avatar.summary && (
                                                <div style={styles.avatarSummary}>
                                                    <div style={styles.avatarSummaryLabel}>Avatar essence</div>
                                                    <div style={styles.avatarSummaryText}>{avatar.summary}</div>
                                                    {avatar.lastUpdated && <div style={styles.avatarUpdated}>Last updated {new Date(avatar.lastUpdated).toLocaleDateString('default', { month: 'long', day: 'numeric', year: 'numeric' })}</div>}
                                                </div>
                                            )}
                                            {[
                                                { key: 'communicationStyle', label: 'Communication style' },
                                                { key: 'motivations', label: 'Core motivations' },
                                                { key: 'resistancePatterns', label: 'Resistance patterns' },
                                                { key: 'resonantLanguage', label: 'Resonant language' },
                                                { key: 'coreBeliefs', label: 'Core beliefs' },
                                                { key: 'relationshipToChange', label: 'Relationship to change' },
                                                { key: 'accountabilityStyle', label: 'Accountability style' },
                                                { key: 'keyContext', label: 'Key context' },
                                                { key: 'coachingApproach', label: 'Coaching approach' },
                                            ].map(section => (
                                                <div key={section.key} style={styles.avatarSection}>
                                                    <div style={styles.avatarSectionHeader}>
                                                        <div style={styles.avatarSectionLabel}>{section.label}</div>
                                                        {editingAvatarSection !== section.key && (
                                                            <button style={styles.cardAction} onClick={() => { setEditingAvatarSection(section.key); setEditingAvatarValue(avatar[section.key] || ''); }}>Edit</button>
                                                        )}
                                                    </div>
                                                    {editingAvatarSection === section.key ? (
                                                        <div>
                                                            <textarea style={{ ...styles.textarea, minHeight: '100px', marginBottom: '8px' }} value={editingAvatarValue} onChange={e => setEditingAvatarValue(e.target.value)} />
                                                            <div style={{ display: 'flex', gap: '8px' }}>
                                                                <button style={styles.saveBtn} onClick={() => handleSaveAvatarSection(section.key)} disabled={savingAvatar}>{savingAvatar ? 'Saving...' : 'Save'}</button>
                                                                <button style={styles.cancelBtn} onClick={() => { setEditingAvatarSection(null); setEditingAvatarValue(''); }}>Cancel</button>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div style={styles.avatarSectionText}>{avatar[section.key] || <span style={{ color: 'var(--text-faint)', fontStyle: 'italic' }}>Not yet captured</span>}</div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
    );

    const recentSessionCard = sessions.length > 0 && (
        <div style={styles.card}>
                                    <div style={styles.cardHeader}>
                                        <span style={styles.cardTitle}>Most recent session</span>
                                        {activeTab !== 'sessions' && <button style={styles.cardAction} onClick={() => handleTabChange('sessions')}>All sessions →</button>}
                                    </div>
                                    <div style={styles.cardBody}>
                                        {(() => {
                                            const s = sessions[0];
                                            return (
                                                <div>
                                                    <div style={styles.sessionItemTitle}>{s.title}</div>
                                                    <div style={styles.sessionItemDate}>{new Date(s.date + 'T12:00:00').toLocaleDateString('default', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</div>
                                                    {s.summary?.overview && <div style={styles.sessionOverview}>{s.summary.overview}</div>}
                                                    {s.summary?.keyThemes?.length > 0 && (
                                                        <div style={styles.tagRow}>
                                                            {s.summary.keyThemes.map((t, i) => <span key={i} style={styles.themeTag}>{t}</span>)}
                                                        </div>
                                                    )}
                                                    <button style={styles.linkBtn} onClick={() => { handleTabChange('sessions'); setSelectedSession(s); }}>
                                                        View full summary →
                                                    </button>
                                                </div>
                                            );
                                        })()}
                                    </div>
                                </div>
    );

    const themesCard = topThemes.length > 0 && (
        <div style={styles.card}>
                                    <div style={styles.cardHeader}><span style={styles.cardTitle}>Recurring themes</span></div>
                                    <div style={styles.cardBody}>
                                        <div style={styles.themeGrid}>
                                            {topThemes.map(([theme, count], i) => (
                                                <div key={i} style={styles.themeItem}>
                                                    <span style={styles.themeText}>{theme}</span>
                                                    <span style={styles.themeCount}>{count}×</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
    );

    const firstName = client.name.split(' ')[0];

    // The client's own first-login answers (portal intentions step). Shown at the
    // top of Overview as the anchor for what this person is working on.
    const answeredIntentions = (client.intentions || []).filter(i => (i.answer || '').trim());
    const intentionsCard = answeredIntentions.length > 0 && (
        <div style={styles.intentionsCard}>
            <div style={styles.intentionsHeader}>◎ {firstName}'s intentions <span style={styles.intentionsSource}>· from their portal</span></div>
            {answeredIntentions.map((item, i) => (
                <div key={i} style={{ marginTop: i === 0 ? 0 : '12px' }}>
                    <div style={styles.intentionsQuestion}>{item.question}</div>
                    <div style={styles.intentionsAnswer}>{item.answer}</div>
                </div>
            ))}
        </div>
    );

    const startCapture = (capture) => {
        if (capture?.type === 'file' && !capture.file) return;
        setPendingCapture(capture);
        setCaptureKey(k => k + 1);
        setShowAddSession(true);
    };

    const closeAddSession = () => { setShowAddSession(false); setPendingCapture(null); };

    const progressContent = (
        <>
        {arcCard}
        {themesCard}
                    {allBreakthroughs.length === 0 ? (
                        <div style={styles.emptyState}>
                            <div style={styles.emptyIcon}>✦</div>
                            <h3 style={styles.emptyTitle}>No breakthrough moments yet</h3>
                            <p style={styles.emptyText}>Breakthrough quotes are extracted from sessions automatically.</p>
                        </div>
                    ) : (
                        <>
                            {starredBreakthroughs.length > 0 && (
                                <div style={styles.card}>
                                    <div style={styles.cardHeader}><span style={styles.cardTitle}>★ Starred moments</span></div>
                                    <div style={styles.cardBody}>
                                        {starredBreakthroughs.map((bq, i) => (
                                            <div key={i} style={{ ...styles.breakthroughCard, marginBottom: '12px' }}>
                                                <div style={styles.breakthroughQuote}>"{bq.quote}"</div>
                                                {bq.context && <div style={styles.breakthroughContext}>{bq.context}</div>}
                                                {bq.significance && <div style={styles.breakthroughSignificance}>{bq.significance}</div>}
                                                <div style={styles.breakthroughMeta}>
                                                    {bq.sessionType === 'group' && <span style={styles.groupSessionTagSmall}>◎ Group</span>}
                                                    {bq.sessionTitle} · {bq.sessionDate ? new Date(bq.sessionDate + 'T12:00:00').toLocaleDateString() : ''}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {allBreakthroughs.filter(b => !b.starred).length > 0 && (
                                <div style={{ ...styles.card, marginTop: '16px' }}>
                                    <div style={styles.cardHeader}><span style={styles.cardTitle}>All moments</span></div>
                                    <div style={styles.cardBody}>
                                        {allBreakthroughs.filter(b => !b.starred).map((bq, i) => (
                                            <div key={i} style={{ ...styles.breakthroughCard, background: 'white', border: '1px solid var(--border)', marginBottom: '12px' }}>
                                                <div style={styles.breakthroughQuote}>"{bq.quote}"</div>
                                                {bq.context && <div style={styles.breakthroughContext}>{bq.context}</div>}
                                                <div style={styles.breakthroughMeta}>
                                                    {bq.sessionType === 'group' && <span style={styles.groupSessionTagSmall}>◎ Group</span>}
                                                    {bq.sessionTitle} · {bq.sessionDate ? new Date(bq.sessionDate + 'T12:00:00').toLocaleDateString() : ''}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </>
                    )}

                    <div style={{ ...styles.card, marginTop: '16px' }}>
                        <div style={styles.cardHeader}><span style={styles.cardTitle}>Long-term goals</span></div>
                        <div style={styles.cardBody}>
                            {(client.long_goals || []).length === 0 ? (
                                <p style={styles.emptyText}>No long-term goals set yet.</p>
                            ) : (
                                (client.long_goals || []).map((g, i) => (
                                    <div key={i} style={styles.goalManageRow}>
                                        <div style={styles.goalText}>{g.text}</div>
                                        <div style={styles.goalActions}>
                                            {['active', 'achieved', 'abandoned'].map(status => (
                                                <button
                                                    key={status}
                                                    style={{ ...styles.goalStatusBtn, ...(g.status === status ? goalStatusStyle(status) : {}) }}
                                                    onClick={() => toggleGoalStatus(i, status)}
                                                >
                                                    {status.charAt(0).toUpperCase() + status.slice(1)}
                                                </button>
                                            ))}
                                            <button style={styles.removeBtn} onClick={() => removeGoal(i)}>×</button>
                                        </div>
                                    </div>
                                ))
                            )}
                            <div style={styles.divider} />
                            <div style={styles.addGoalRow}>
                                <input
                                    style={{ ...styles.input, flex: 1 }}
                                    placeholder="Add a long-term goal..."
                                    value={newLongGoal}
                                    onChange={e => setNewLongGoal(e.target.value)}
                                    onKeyDown={e => e.key === 'Enter' && addLongGoal()}
                                />
                                <button style={styles.addGoalBtn} onClick={addLongGoal} disabled={saving}>Add</button>
                            </div>
                        </div>
                    </div>

                    {/* Client-Submitted Goals */}
                    <div style={{ ...styles.card, marginTop: '16px' }}>
                        <div style={styles.cardHeader}>
                            <span style={styles.cardTitle}>Client-submitted goals</span>
                        </div>
                        <div style={styles.cardBody}>
                            {clientGoals.filter(g => g.source === 'client').length === 0 ? (
                                <p style={styles.emptyText}>No goals submitted by this client yet. They can add goals from their app.</p>
                            ) : (
                                clientGoals.filter(g => g.source === 'client').map(g => (
                                    <div key={g.id} style={styles.goalManageRow}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                                            <span style={{ fontSize: '11px', background: 'var(--mint)', color: 'var(--sage)', padding: '2px 8px', borderRadius: '10px', fontWeight: '600', flexShrink: 0 }}>Client</span>
                                            <div style={styles.goalText}>{g.text}</div>
                                        </div>
                                        <div style={styles.goalActions}>
                                            {['active', 'completed', 'paused'].map(status => (
                                                <button
                                                    key={status}
                                                    style={{ ...styles.goalStatusBtn, ...(g.status === status ? goalStatusStyle(status) : {}) }}
                                                    onClick={() => changeClientGoalStatus(g.id, status)}
                                                >
                                                    {status.charAt(0).toUpperCase() + status.slice(1)}
                                                </button>
                                            ))}
                                            <button style={styles.removeBtn} onClick={() => removeClientGoal(g.id)}>×</button>
                                        </div>
                                    </div>
                                ))
                            )}
                            <div style={styles.divider} />
                            <div style={{ fontSize: '12px', color: 'var(--text-soft)', marginBottom: '12px' }}>Add a coach-set goal visible to this client in their app</div>
                            <div style={styles.addGoalRow}>
                                <input
                                    style={{ ...styles.input, flex: 1 }}
                                    placeholder="Add a goal for this client..."
                                    value={newClientGoalText}
                                    onChange={e => setNewClientGoalText(e.target.value)}
                                    onKeyDown={e => e.key === 'Enter' && addNewClientGoal()}
                                />
                                <button style={styles.addGoalBtn} onClick={addNewClientGoal}>Add</button>
                            </div>
                        </div>
                    </div>
        </>
    );

    const tabs = [
        { id: 'sessions', label: `Sessions (${totalSessions})` },
        { id: 'messages', label: unreadCount > 0 ? `Messages 🔴` : `Messages (${messages.length})` },
        { id: 'overview', label: 'Overview' },
        ...(SHOW_PROGRESS_TAB ? [{ id: 'progress', label: 'Progress' }] : []),
        { id: 'clientinfo', label: 'Client Info' },
    ];

    return (
        <div style={styles.page}>

            {error && <div style={styles.errorAlert}>{error}<button onClick={() => setError('')} style={styles.errorClose}>×</button></div>}

            {/* Client header */}
            <div style={styles.clientHeader}>
                <button style={styles.backBtn} onClick={() => navigate('/clients')}>← Clients</button>
                <div style={styles.clientHeaderInner}>
                    <div style={styles.clientAvatarLarge}>{initials}</div>
                    <div style={styles.clientHeaderInfo}>
                        <div style={styles.clientName}>{client.name}</div>
                        <div style={styles.clientMeta}>
                            {(client.offerings || []).map(o => (
                                <span key={o.id} style={o.format === 'group' ? styles.offeringBadgeGroup : styles.offeringBadge}>{o.name}</span>
                            ))}
                            {clientGroups.map(g => (
                                <span key={g.id} style={styles.headerBadge} onClick={() => navigate(`/groups/${g.id}`)}>{g.name}</span>
                            ))}
                            <span style={styles.sinceText}>
                                {totalSessions === 0
                                    ? '0 sessions'
                                    : `since ${new Date(firstSession + 'T12:00:00').toLocaleDateString('default', { month: 'short', year: 'numeric' })}`}
                            </span>
                        </div>
                    </div>
                    {/* Secondary actions live in a "…" menu instead of header buttons */}
                    <div ref={moreMenuRef} style={{ position: 'relative', flexShrink: 0 }}>
                        <button style={styles.moreBtn} onClick={() => setShowMoreMenu(o => !o)} aria-label="More actions">⋯</button>
                        {showMoreMenu && (
                            <div style={styles.moreMenu}>
                                <button style={styles.moreMenuItem} onClick={() => { setShowMoreMenu(false); setShowProgressExport(true); }}>
                                    Export report
                                </button>
                                <button style={{ ...styles.moreMenuItem, color: 'var(--red)' }} onClick={() => { setShowMoreMenu(false); setShowDeleteModal(true); setDeleteConfirmText(''); }}>
                                    Delete client
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div style={styles.tabsWrap}>
                <div style={styles.tabs}>
                    {tabs.map(tab => (
                        <button
                            key={tab.id}
                            style={{ ...styles.tab, ...(activeTab === tab.id ? styles.tabActive : {}) }}
                            onClick={() => handleTabChange(tab.id)}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Tab content */}
            <div style={styles.tabContent}>

                {/* OVERVIEW — a light snapshot; the full avatar opens as its own view */}
                {activeTab === 'overview' && (showFullAvatar ? (
                    <div>
                        <button style={styles.backBtn} onClick={() => setShowFullAvatar(false)}>← Overview</button>
                        {fullAvatarCard}
                    </div>
                ) : (
                    <div style={styles.snapshotCol}>
                        {intentionsCard}
                        {arcGenerated && transformationArc?.arc?.headline && (
                            <div style={styles.snapshotLine}>{transformationArc.arc.headline}</div>
                        )}

                        {recentSessionCard || (
                            <div style={styles.card}>
                                <div style={styles.cardBody}>
                                    <p style={{ ...styles.emptyText, marginBottom: '12px' }}>No sessions yet. Once {firstName} has a session, their latest one shows up here.</p>
                                    <button style={styles.emptyBtn} onClick={() => handleTabChange('sessions')}>Add a session</button>
                                </div>
                            </div>
                        )}

                        <div style={styles.card}>
                            <div style={styles.cardHeader}>
                                <span style={styles.cardTitle}>◈ Client avatar</span>
                                {avatar && !buildingAvatar && (
                                    <button style={styles.cardAction} onClick={() => setShowFullAvatar(true)}>View full avatar →</button>
                                )}
                            </div>
                            <div style={styles.cardBody}>
                                {buildingAvatar ? (
                                    <div style={styles.arcLoading}>
                                        <div style={styles.arcLoadingText}>Building {client.name}'s avatar...</div>
                                        <div style={styles.progressBar}><div style={styles.progressFill} /></div>
                                    </div>
                                ) : avatar ? (
                                    <div style={styles.avatarSummaryText}>{avatar.summary || 'Open the full avatar to see each section.'}</div>
                                ) : sessions.length > 0 ? (
                                    <div style={styles.arcPrompt}>
                                        <div style={styles.arcPromptText}>Build a deep coaching avatar for {client.name}, synthesized from all {sessions.length} sessions and messages.</div>
                                        <button style={styles.arcBtn} onClick={handleBuildAvatar}>Build avatar</button>
                                    </div>
                                ) : (
                                    <p style={styles.emptyText}>Record at least one session to build their avatar.</p>
                                )}
                            </div>
                        </div>

                        {!SHOW_PROGRESS_TAB && progressContent}
                    </div>
                ))}

                {/* SESSIONS — latest summary, quick capture, then history */}
                {activeTab === 'sessions' && (
                    <div>
                        {selectedSession ? (
                            <div>
                                <button style={styles.backBtn} onClick={() => setSelectedSession(null)}>← All sessions</button>
                                <SessionDetail
                                    session={selectedSession}
                                    client={client}
                                    onExport={(session) => { setExportingSession(session); setShowSessionExport(true); }}
                                />
                            </div>
                        ) : showAddSession ? (
                            <div style={styles.card}>
                                <div style={{ padding: '24px' }}>
                                    <SessionForm
                                        key={captureKey}
                                        initialClientIds={[client.id]}
                                        initialCapture={pendingCapture}
                                        lockSelection
                                        lockedLabel={client.name}
                                        embedded
                                        onCancel={closeAddSession}
                                        onSaved={() => { closeAddSession(); fetchData(); }}
                                    />
                                </div>
                            </div>
                        ) : (
                            <>
                                {recentSessionCard}

                                <div style={styles.card}>
                                    <div style={{ padding: '20px 24px' }}>
                                        <div style={styles.captureLabel}>Add a session for {firstName}</div>
                                        <div
                                            style={{ ...styles.captureDrop, ...(captureDragging ? styles.captureDropActive : {}) }}
                                            onClick={() => captureInputRef.current?.click()}
                                            onDragOver={e => { e.preventDefault(); setCaptureDragging(true); }}
                                            onDragLeave={() => setCaptureDragging(false)}
                                            onDrop={e => { e.preventDefault(); setCaptureDragging(false); startCapture({ type: 'file', file: e.dataTransfer.files[0] }); }}
                                        >
                                            <div style={styles.captureIcon}>☁️</div>
                                            <div style={styles.captureTitle}>Drag &amp; drop a recording or transcript</div>
                                            <div style={styles.captureSub}>or <span style={styles.captureBrowse}>click to browse</span></div>
                                            <input
                                                ref={captureInputRef}
                                                type="file"
                                                accept="audio/*,.txt,.md,.vtt,.srt,text/*"
                                                style={{ display: 'none' }}
                                                onChange={e => { startCapture({ type: 'file', file: e.target.files[0] }); e.target.value = ''; }}
                                            />
                                        </div>
                                        <div style={styles.captureOr}><span style={styles.captureOrText}>or, live right now</span></div>
                                        <div style={styles.captureLiveRow} onClick={() => startCapture({ type: 'record' })}>
                                            <span style={styles.captureLiveDot} />
                                            <div style={{ flex: 1 }}>
                                                <div style={styles.captureLiveTitle}>Start recording</div>
                                                <div style={styles.captureLiveSub}>Capture a live call, tag it after</div>
                                            </div>
                                            <span>🎙️</span>
                                        </div>
                                        <div style={styles.captureFooter}>
                                            You'll pick the offering type right after.{' '}
                                            <button style={styles.captureNotesLink} onClick={() => startCapture(null)}>Or add notes instead</button>
                                        </div>
                                    </div>
                                </div>

                                <div style={styles.sectionLabel}>Session history</div>
                                {sessions.length === 0 ? (
                                    <div style={{ ...styles.card, ...styles.historyEmpty }}>
                                        No sessions yet. Your first one will appear here.
                                    </div>
                                ) : (
                                    sessions.map(s => (
                                        <div key={s.id} style={styles.sessionCard} className="tc-card-hover" onClick={() => setSelectedSession(s)}>
                                            <div style={styles.sessionDateBlock}>
                                                <div style={styles.sessionDay}>{new Date(s.date + 'T12:00:00').getDate()}</div>
                                                <div style={styles.sessionMonth}>{new Date(s.date + 'T12:00:00').toLocaleString('default', { month: 'short' })}</div>
                                                <div style={styles.sessionYear}>{new Date(s.date + 'T12:00:00').getFullYear()}</div>
                                            </div>
                                            <div style={styles.sessionInfo}>
                                                <div style={styles.sessionTitle}>{s.title || 'Session'}</div>
                                                {s.group_context?.name && <span style={styles.groupBadge}>🔗 {s.group_context.name} member at the time</span>}
                                                {s.session_type === 'group' && s.participant_names?.length > 0 && (
                                                    <div style={styles.sessionParticipants}>{s.participant_names.join(' · ')}</div>
                                                )}
                                                <div style={styles.sessionPreview}>{s.summary?.overview?.slice(0, 120)}...</div>
                                                <div style={styles.sessionTags}>
                                                    {s.session_type === 'group' && <span style={styles.groupSessionTag}>◎ Group</span>}
                                                    {s.summary?.keyThemes?.slice(0, 3).map((t, i) => <span key={i} style={styles.themeTag}>{t}</span>)}
                                                    {(s.goals || []).length > 0 && <span style={styles.goalTag}>{(s.goals || []).filter(g => g.status === 'met').length}/{(s.goals || []).length} goals</span>}
                                                    {(s.breakthrough_quotes || []).filter(b => b.starred).length > 0 && <span style={styles.breakthroughTag}>★ {(s.breakthrough_quotes || []).filter(b => b.starred).length}</span>}
                                                </div>
                                            </div>
                                            <div style={styles.chevron}>→</div>
                                        </div>
                                    ))
                                )}
                            </>
                        )}
                    </div>
                )}

                {/* PROGRESS — arc + themes, breakthroughs, goals */}
                {activeTab === 'progress' && SHOW_PROGRESS_TAB && progressContent}

                {/* CLIENT INFO (Notes + Intake + portal access) */}
                {activeTab === 'clientinfo' && (
                    <>
                    <div style={styles.card}>
                        <div style={styles.cardHeader}>
                            <span style={styles.cardTitle}>Client notes</span>
                            {!editingNotes && <button style={styles.cardAction} onClick={() => setEditingNotes(true)}>Edit</button>}
                        </div>
                        <div style={styles.cardBody}>
                            <div style={styles.notesSection}>
                                <div style={styles.notesLabel}>Ongoing notes</div>
                                {editingNotes ? (
                                    <>
                                        <textarea
                                            style={{ ...styles.textarea, minHeight: '200px' }}
                                            value={notesValue}
                                            onChange={e => setNotesValue(e.target.value)}
                                        />
                                        <div style={{ display: 'flex', gap: '10px', marginTop: '12px' }}>
                                            <button style={styles.saveBtn} onClick={saveNotes} disabled={saving}>{saving ? 'Saving...' : 'Save notes'}</button>
                                            <button style={styles.cancelBtn} onClick={() => { setEditingNotes(false); setNotesValue(client.notes || ''); }}>Cancel</button>
                                        </div>
                                    </>
                                ) : (
                                    <div style={styles.notesText}>{client.notes || <span style={{ color: 'var(--text-faint)', fontStyle: 'italic' }}>No notes recorded.</span>}</div>
                                )}
                            </div>
                        </div>
                    </div>

                    {intentionsCard && <div style={{ marginTop: '16px' }}>{intentionsCard}</div>}

                    <div style={{ ...styles.card, marginTop: '16px' }}>
                        <div style={styles.cardHeader}><span style={styles.cardTitle}>Intake information</span></div>
                        <div style={styles.cardBody}>
                            <div style={styles.notesSection}>
                                <div style={styles.notesLabel}>Name</div>
                                <div style={styles.notesText}>{client.name}</div>
                            </div>
                            <div style={styles.divider} />
                            <div style={styles.notesSection}>
                                <div style={styles.notesLabel}>Email</div>
                                <div style={styles.notesText}>{client.email || <span style={{ color: 'var(--text-faint)', fontStyle: 'italic' }}>Not provided.</span>}</div>
                            </div>
                            <div style={styles.divider} />
                            <div style={styles.notesSection}>
                                <div style={styles.notesLabel}>Username</div>
                                <div style={styles.notesText}>{client.username || <span style={{ color: 'var(--text-faint)', fontStyle: 'italic' }}>Not set.</span>}</div>
                            </div>
                            <div style={styles.divider} />
                            <div style={styles.notesSection}>
                                <div style={styles.notesLabel}>Timezone</div>
                                <div style={styles.notesText}>{client.timezone || <span style={{ color: 'var(--text-faint)', fontStyle: 'italic' }}>Not set.</span>}</div>
                            </div>
                            <div style={styles.divider} />
                            <div style={styles.notesSection}>
                                <div style={styles.notesLabel}>Intake responses</div>
                                <div style={styles.notesText}>{client.intake_background || <span style={{ color: 'var(--text-faint)', fontStyle: 'italic' }}>No intake background recorded.</span>}</div>
                            </div>
                        </div>
                    </div>

                        {/* Portal access + danger zone (formerly the Settings tab) */}
                        <div style={{ ...styles.card, marginTop: '16px' }}>
                            <div style={styles.cardHeader}><span style={styles.cardTitle}>Client portal access</span></div>
                            <div style={styles.cardBody}>
                                <div style={{ marginBottom: '16px' }}>
                                    <div style={styles.notesLabel}>Client username</div>
                                    <div style={{ fontSize: '14px', color: 'var(--text-dark)', marginTop: '6px' }}>
                                        {client.username || <span style={{ color: 'var(--text-faint)', fontStyle: 'italic' }}>Not set — the client can choose one from their portal.</span>}
                                    </div>
                                </div>
                                <div style={{ marginBottom: '16px' }}>
                                    <div style={styles.notesLabel}>Client email</div>
                                    <div style={{ display: 'flex', gap: '8px', marginTop: '6px' }}>
                                        <input
                                            type="email"
                                            style={{ ...styles.input, flex: 1 }}
                                            placeholder="client@example.com"
                                            value={emailValue}
                                            onChange={e => setEmailValue(e.target.value)}
                                        />
                                        <button style={styles.cancelBtn} onClick={saveEmail} disabled={saving || emailValue === (client.email || '')}>Save</button>
                                        <button style={styles.saveBtn} onClick={handleSendInvite} disabled={sendingInvite || !emailValue.trim()}>
                                            {sendingInvite ? 'Sending...' : accessCode ? 'Resend invite' : 'Send portal invite'}
                                        </button>
                                    </div>
                                </div>
                                {accessCode ? (
                                    <div>
                                        <div style={styles.notesLabel}>Share this link with {client.name}</div>
                                        <div style={styles.accessCodeBox}>{`${PORTAL_BASE_URL}/portal/${accessCode}`}</div>
                                        <div style={{ display: 'flex', gap: '8px', marginTop: '12px', flexWrap: 'wrap' }}>
                                            <button style={styles.saveBtn} onClick={() => { navigator.clipboard.writeText(`${PORTAL_BASE_URL}/portal/${accessCode}`); showToast('Link copied'); }}>
                                                Copy link
                                            </button>
                                            <button style={styles.cancelBtn} onClick={() => setAccessCode('')}>Change code</button>
                                            <button style={styles.cancelBtn} onClick={handleResetPortalPassword} disabled={resettingPassword}>
                                                {resettingPassword ? 'Resetting...' : 'Reset password'}
                                            </button>
                                            <button style={styles.cancelBtn} onClick={handleSendMagicLink} disabled={sendingMagicLink || !emailValue.trim()}>
                                                {sendingMagicLink ? 'Sending...' : 'Send magic link'}
                                            </button>
                                        </div>
                                        <div style={{ fontSize: '12px', color: 'var(--text-faint)', marginTop: '8px' }}>
                                            A magic link signs {client.name} in instantly with no password — expires in 15 minutes and works once.
                                        </div>
                                    </div>
                                ) : (
                                    <div>
                                        <p style={{ ...styles.emptyText, marginBottom: '12px' }}>Create a memorable login code for {client.name}.</p>
                                        <div style={{ display: 'flex', gap: '8px' }}>
                                            <input
                                                style={{ ...styles.input, flex: 1 }}
                                                placeholder="e.g. sarah-2024"
                                                value={customCode}
                                                onChange={e => setCustomCode(e.target.value.toLowerCase().replace(/\s+/g, '-'))}
                                                onKeyDown={e => e.key === 'Enter' && handleGenerateCode()}
                                            />
                                            <button style={styles.saveBtn} onClick={handleGenerateCode} disabled={generatingCode || !customCode.trim()}>
                                                {generatingCode ? 'Saving...' : 'Set code'}
                                            </button>
                                        </div>
                                        <div style={{ fontSize: '12px', color: 'var(--text-faint)', marginTop: '8px' }}>Letters, numbers, and hyphens only.</div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Danger zone */}
                        <div style={{ ...styles.card, marginTop: '16px', borderColor: 'var(--red-light)' }}>
                            <div style={styles.cardHeader}><span style={{ ...styles.cardTitle, color: 'var(--red)' }}>Danger zone</span></div>
                            <div style={styles.cardBody}>
                                <p style={{ ...styles.emptyText, marginBottom: '12px' }}>
                                    Permanently delete {client.name} and everything tied to them — sessions, messages, goals, and all past conversations. This cannot be undone.
                                </p>
                                <button
                                    style={{ padding: '10px 18px', borderRadius: '8px', background: 'none', border: '1px solid var(--red)', color: 'var(--red)', fontFamily: "'DM Sans', sans-serif", fontSize: '13.5px', fontWeight: '500', cursor: 'pointer' }}
                                    onClick={() => { setShowDeleteModal(true); setDeleteConfirmText(''); }}
                                >
                                    Delete client
                                </button>
                            </div>
                        </div>
                    </>
                )}

                {/* MESSAGES */}
                {activeTab === 'messages' && (
                    <div>
                        {/* Telegram */}
                        {accessCode && (
                            <div style={{ background: 'var(--mint)', border: '1px solid var(--border)', borderRadius: 10, padding: '12px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
                                <span style={{ fontSize: 20 }}>✈️</span>
                                <div style={{ flex: 1 }}>
                                    <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-dark)' }}>Telegram</div>
                                    <div style={{ fontSize: 12, color: 'var(--text-soft)' }}>
                                        {telegramConnected
                                            ? 'Client is connected — messages sync automatically'
                                            : 'Share this link so your client can message via Telegram'}
                                    </div>
                                </div>
                                {telegramConnected ? (
                                    <button onClick={handleDisconnectTelegram} style={{ fontSize: 12, color: 'var(--red)', background: 'none', border: 'none', cursor: 'pointer' }}>
                                        Disconnect
                                    </button>
                                ) : (
                                    <button onClick={() => { navigator.clipboard.writeText(telegramLink); showToast('Telegram link copied'); }} style={styles.saveBtn}>
                                        Copy link
                                    </button>
                                )}
                            </div>
                        )}

                        {/* Messages */}
                        <MessageThread clientId={id} client={client} onMessageSent={fetchData} />
                    </div>
                )}

            </div>

            {/* Modals */}
            {showSessionExport && exportingSession && (
                <SessionExportModal session={exportingSession} client={client} practiceName={profile?.practice_name || ''} onClose={() => { setShowSessionExport(false); setExportingSession(null); }} />
            )}
            {showProgressExport && (
                <ProgressReportExportModal client={client} sessions={sessions} practiceName={profile?.practice_name || ''} transformationArc={transformationArc} onClose={() => setShowProgressExport(false)} />
            )}

            {showDeleteModal && (
                <div style={styles.deleteModalOverlay} onClick={() => setShowDeleteModal(false)}>
                    <div style={styles.deleteModal} onClick={e => e.stopPropagation()}>
                        <h3 style={styles.deleteModalTitle}>Delete {client.name}?</h3>
                        <p style={styles.deleteModalBody}>
                            This will permanently delete {client.name} and everything tied to them — sessions, messages, goals, and all past conversations. <strong>This cannot be undone.</strong>
                        </p>
                        <p style={styles.deleteModalPrompt}>Type <strong>DELETE</strong> to confirm:</p>
                        <input
                            style={{ ...styles.input, borderColor: deleteConfirmText === 'DELETE' ? 'var(--red)' : 'var(--border)', marginBottom: '20px' }}
                            value={deleteConfirmText}
                            onChange={e => setDeleteConfirmText(e.target.value)}
                            placeholder="DELETE"
                            autoFocus
                        />
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <button
                                style={{ ...styles.deleteConfirmBtn, opacity: deleteConfirmText === 'DELETE' && !deletingClient ? 1 : 0.4 }}
                                onClick={handleDeleteClient}
                                disabled={deleteConfirmText !== 'DELETE' || deletingClient}
                            >
                                {deletingClient ? 'Deleting...' : `Yes, delete ${client.name}`}
                            </button>
                            <button style={styles.cancelBtn} onClick={() => setShowDeleteModal(false)}>
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// ── Helper ──
const goalStatusStyle = (status) => {
    if (status === 'achieved') return { background: 'var(--mint)', borderColor: 'var(--sage)', color: 'var(--sage)' };
    if (status === 'abandoned') return { background: 'var(--red-light)', borderColor: 'var(--red)', color: 'var(--red)' };
    return { background: '#FAF4EE', borderColor: '#C4956A', color: '#C4956A' };
};

// ── Session Detail ──
function SessionDetail({ session, client, onExport }) {
    const s = session.summary || {};
    const goals = session.goals || [];
    const breakthroughs = session.breakthrough_quotes || [];
    const hasTranscript = session.transcript && session.transcript.trim().length > 0;
    const hasSummary = session.summary !== null;

    const downloadTranscript = () => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(new Blob([session.transcript], { type: 'text/plain' }));
        a.download = `transcript_${session.title?.replace(/\s+/g, '_') || 'session'}_${session.date}.txt`;
        a.click();
    };

    return (
        <div>
            <div style={detailStyles.header}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                    <div style={detailStyles.title}>{session.title || 'Session'}</div>
                    {session.session_type === 'group' && (
                        <span style={detailStyles.groupBadge}>◎ Group session</span>
                    )}
                    {session.group_context?.name && <span style={detailStyles.groupBadge}>🔗 {session.group_context.name} member at the time</span>}
                </div>
                <div style={detailStyles.date}>{new Date(session.date + 'T12:00:00').toLocaleDateString('default', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</div>
                {session.session_type === 'group' && session.participant_names?.length > 0 && (
                    <div style={detailStyles.participants}>Participants: {session.participant_names.join(' · ')}</div>
                )}
                <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                    {hasTranscript && <button style={detailStyles.actionBtn} onClick={downloadTranscript}>↓ Transcript</button>}
                    {onExport && <button style={detailStyles.actionBtn} onClick={() => onExport(session)}>Export PDF</button>}
                </div>
            </div>

            {!hasSummary && (
                <div style={detailStyles.alert}>No AI summary was generated for this session.</div>
            )}

            {hasSummary && (
                <>
                    {s.overview && <div style={detailStyles.card}><div style={detailStyles.sectionTitle}>Overview</div><div style={detailStyles.text}>{s.overview}</div></div>}
                    {s.keyThemes?.length > 0 && (
                        <div style={detailStyles.card}>
                            <div style={detailStyles.sectionTitle}>Key themes</div>
                            <div style={detailStyles.tagRow}>{s.keyThemes.map((t, i) => <span key={i} style={detailStyles.tag}>{t}</span>)}</div>
                        </div>
                    )}
                    {s.breakthroughs && <div style={detailStyles.card}><div style={detailStyles.sectionTitle}>Breakthroughs</div><div style={detailStyles.text}>{s.breakthroughs}</div></div>}
                    {s.challenges && <div style={detailStyles.card}><div style={detailStyles.sectionTitle}>Challenges</div><div style={detailStyles.text}>{s.challenges}</div></div>}
                    {s.clientGrowth && <div style={detailStyles.card}><div style={detailStyles.sectionTitle}>Client growth</div><div style={detailStyles.text}>{s.clientGrowth}</div></div>}
                    {s.clinicalObservations && <div style={detailStyles.card}><div style={detailStyles.sectionTitle}>Coaching observations</div><div style={detailStyles.text}>{s.clinicalObservations}</div></div>}
                    {s.actionItems?.length > 0 && (
                        <div style={detailStyles.card}>
                            <div style={detailStyles.sectionTitle}>Action items</div>
                            <ul style={detailStyles.list}>{s.actionItems.map((a, i) => <li key={i}>{a}</li>)}</ul>
                        </div>
                    )}
                    {goals.length > 0 && (
                        <div style={detailStyles.card}>
                            <div style={detailStyles.sectionTitle}>Session goals</div>
                            {goals.map((g, i) => {
                                const color = g.status === 'met' ? 'var(--sage)' : g.status === 'partial' ? '#C4956A' : g.status === 'unmet' ? 'var(--red)' : 'var(--text-faint)';
                                const label = g.status === 'met' ? 'Met' : g.status === 'partial' ? 'Partial' : g.status === 'unmet' ? 'Not met' : 'Pending';
                                return (
                                    <div key={i} style={detailStyles.goalRow}>
                                        <div style={detailStyles.goalText}>{g.text}</div>
                                        <span style={{ ...detailStyles.goalTag, color, borderColor: color }}>{label}</span>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                    {breakthroughs.length > 0 && (
                        <div style={detailStyles.card}>
                            <div style={detailStyles.sectionTitle}>
                                {session.session_type === 'group' ? 'Your moments in this session' : 'Breakthrough moments'}
                            </div>
                            {breakthroughs.map((bq, i) => (
                                <div key={i} style={detailStyles.breakthroughCard}>
                                    {bq.starred && <span style={detailStyles.star}>★</span>}
                                    <div style={detailStyles.quote}>"{bq.quote}"</div>
                                    {bq.context && <div style={detailStyles.meta}>{bq.context}</div>}
                                </div>
                            ))}
                        </div>
                    )}
                    {s.coachRecommendations && <div style={detailStyles.card}><div style={detailStyles.sectionTitle}>Next session recommendations</div><div style={detailStyles.text}>{s.coachRecommendations}</div></div>}
                    {s.nextSessionPrompts?.length > 0 && (
                        <div style={detailStyles.card}>
                            <div style={detailStyles.sectionTitle}>Opening prompts</div>
                            {s.nextSessionPrompts.map((p, i) => <div key={i} style={detailStyles.prompt}>{p}</div>)}
                        </div>
                    )}
                    {session.post_reflection && <div style={detailStyles.card}><div style={detailStyles.sectionTitle}>Coach reflection</div><div style={detailStyles.text}>{session.post_reflection}</div></div>}
                    {hasTranscript && (
                        <div style={detailStyles.card}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                                <div style={detailStyles.sectionTitle}>Transcript</div>
                                <button style={detailStyles.actionBtn} onClick={downloadTranscript}>↓ Download</button>
                            </div>
                            <div style={detailStyles.transcript}>{session.transcript.slice(0, 800)}{session.transcript.length > 800 ? '...' : ''}</div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

const detailStyles = {
    header: { marginBottom: '24px' },
    title: { fontFamily: "'Lora', Georgia, serif", fontSize: '24px', fontWeight: '400', color: 'var(--text-dark)', marginBottom: '4px' },
    date: { fontSize: '13px', color: 'var(--text-soft)' },
    groupBadge: { background: 'rgba(196,149,106,0.15)', color: 'var(--gold)', border: '1px solid rgba(196,149,106,0.4)', padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '600', whiteSpace: 'nowrap' },
    participants: { fontSize: '13px', color: 'var(--text-soft)', marginTop: '6px', fontStyle: 'italic' },
    actionBtn: { padding: '7px 14px', borderRadius: '8px', background: 'white', border: '1px solid var(--border)', color: 'var(--text-mid)', fontFamily: "'DM Sans', sans-serif", fontSize: '12.5px', cursor: 'pointer' },
    alert: { background: '#FAF4EE', border: '1px solid rgba(196,149,106,0.3)', borderRadius: '10px', padding: '16px 20px', marginBottom: '20px', fontSize: '14px', color: 'var(--text-mid)' },
    card: { background: 'white', borderRadius: '12px', border: '1px solid var(--border)', padding: '20px 24px', marginBottom: '16px' },
    sectionTitle: { fontFamily: "'Lora', Georgia, serif", fontSize: '15px', color: 'var(--sage)', marginBottom: '10px', paddingBottom: '8px', borderBottom: '1px solid var(--border-light)' },
    text: { fontSize: '14px', lineHeight: 1.75, color: 'var(--text-mid)' },
    tagRow: { display: 'flex', gap: '8px', flexWrap: 'wrap' },
    tag: { background: 'var(--mint)', color: 'var(--sage)', padding: '4px 12px', borderRadius: '20px', fontSize: '12px' },
    list: { paddingLeft: '20px', fontSize: '14px', color: 'var(--text-mid)', lineHeight: 1.8 },
    goalRow: { display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 0', borderBottom: '1px solid var(--border-light)' },
    goalText: { flex: 1, fontSize: '14px' },
    goalTag: { padding: '3px 10px', borderRadius: '20px', border: '1px solid', fontSize: '11.5px' },
    breakthroughCard: { background: 'var(--gold-pale)', borderRadius: '8px', padding: '12px 14px', marginBottom: '10px', position: 'relative' },
    star: { position: 'absolute', top: '10px', right: '12px', color: '#C9A84C', fontSize: '14px' },
    quote: { fontSize: '14px', fontStyle: 'italic', color: 'var(--text-dark)', lineHeight: 1.6, marginBottom: '4px' },
    meta: { fontSize: '12px', color: 'var(--text-soft)' },
    prompt: { background: 'var(--gold-pale)', borderLeft: '3px solid var(--gold)', borderRadius: '0 8px 8px 0', padding: '10px 14px', marginBottom: '10px', fontSize: '13.5px', color: 'var(--text-mid)', fontStyle: 'italic' },
    transcript: { background: 'var(--cream)', borderRadius: '8px', padding: '14px', fontFamily: 'monospace', fontSize: '12px', color: 'var(--text-mid)', lineHeight: 1.7, maxHeight: '300px', overflowY: 'auto' },
};

const styles = {
    page: { padding: '28px 36px', fontFamily: "'DM Sans', sans-serif", maxWidth: '1100px' },
    errorAlert: { background: 'var(--red-light)', color: 'var(--red)', padding: '12px 16px', borderRadius: '8px', fontSize: '13.5px', marginBottom: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
    errorClose: { background: 'none', border: 'none', color: 'var(--red)', fontSize: '18px', cursor: 'pointer' },
    backBtn: { background: 'none', border: '1px solid var(--border)', borderRadius: '8px', padding: '7px 14px', fontSize: '13px', color: 'var(--text-mid)', cursor: 'pointer', marginBottom: '20px', fontFamily: "'DM Sans', sans-serif" },
    clientHeader: { background: 'white', borderRadius: '16px', border: '1px solid var(--border)', padding: '24px 28px', marginBottom: '24px', boxShadow: 'var(--shadow-sm)' },
    clientHeaderInner: { display: 'flex', alignItems: 'flex-start', gap: '20px' },
    clientAvatarLarge: { width: '56px', height: '56px', borderRadius: '50%', background: 'var(--mint)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', fontWeight: '600', color: 'var(--sage)', flexShrink: 0, border: '2px solid var(--border)' },
    clientHeaderInfo: { flex: 1 },
    clientName: { fontFamily: "'Lora', Georgia, serif", fontSize: '26px', fontWeight: '400', color: 'var(--text-dark)', marginBottom: '6px' },
    clientMeta: { fontSize: '13px', color: 'var(--text-soft)', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '12px' },
    modalityPill: { background: 'var(--mint)', color: 'var(--sage)', borderRadius: '20px', fontSize: '11.5px', fontWeight: '500', padding: '3px 10px' },
    sinceText: { color: 'var(--text-faint)' },
    clientStats: { display: 'flex', gap: '8px', flexWrap: 'wrap' },
    statChip: { display: 'flex', alignItems: 'center', gap: '5px', background: 'var(--cream)', border: '1px solid var(--border)', borderRadius: '20px', padding: '4px 12px' },
    statChipValue: { fontSize: '13px', fontWeight: '600', color: 'var(--sage)' },
    statChipLabel: { fontSize: '11px', color: 'var(--text-soft)' },
    clientHeaderActions: { display: 'flex', flexDirection: 'column', gap: '8px', flexShrink: 0 },
    newSessionBtn: { padding: '10px 18px', borderRadius: '8px', background: 'var(--sage)', border: 'none', color: 'white', fontFamily: "'DM Sans', sans-serif", fontSize: '13px', cursor: 'pointer', fontWeight: '500', whiteSpace: 'nowrap' },
    exportBtn: { padding: '10px 18px', borderRadius: '8px', background: 'none', border: '1px solid var(--border)', color: 'var(--text-mid)', fontFamily: "'DM Sans', sans-serif", fontSize: '13px', cursor: 'pointer', whiteSpace: 'nowrap' },
    offeringBadge: { background: 'var(--gold-pale)', color: 'var(--gold)', padding: '2px 10px', borderRadius: '20px', fontSize: '11.5px', fontWeight: '500' },
    offeringBadgeGroup: { background: 'var(--border-light)', color: 'var(--text-mid)', padding: '2px 10px', borderRadius: '20px', fontSize: '11.5px', fontWeight: '500' },
    headerBadge: { background: 'var(--mint)', color: 'var(--sage)', padding: '2px 10px', borderRadius: '20px', fontSize: '11.5px', fontWeight: '500', cursor: 'pointer' },
    moreBtn: { width: '36px', height: '36px', borderRadius: '8px', background: 'none', border: '1px solid var(--border)', color: 'var(--text-mid)', fontSize: '18px', lineHeight: 1, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" },
    moreMenu: { position: 'absolute', top: 'calc(100% + 4px)', right: 0, zIndex: 50, background: 'white', border: '1px solid var(--border)', borderRadius: '10px', boxShadow: 'var(--shadow-lg)', padding: '6px', minWidth: '170px' },
    moreMenuItem: { display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none', borderRadius: '6px', padding: '9px 12px', fontSize: '13.5px', color: 'var(--text-dark)', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" },
    intentionsCard: { background: 'var(--mint)', border: '1px solid var(--border)', borderRadius: '14px', padding: '18px 22px', marginBottom: '20px' },
    intentionsHeader: { fontSize: '13px', fontWeight: '600', color: 'var(--sage)', marginBottom: '12px' },
    intentionsSource: { fontWeight: '400', color: 'var(--text-soft)' },
    intentionsQuestion: { fontSize: '12.5px', color: 'var(--sage)' },
    intentionsAnswer: { fontSize: '14.5px', color: 'var(--text-dark)', lineHeight: 1.55, marginTop: '2px' },
    snapshotCol: { display: 'flex', flexDirection: 'column', maxWidth: '760px' },
    snapshotLine: { fontFamily: "'Lora', Georgia, serif", fontSize: '17px', color: 'var(--text-dark)', lineHeight: 1.5, margin: '0 2px 20px' },
    sectionLabel: { fontSize: '13.5px', fontWeight: '600', color: 'var(--text-dark)', margin: '4px 2px 12px' },
    historyEmpty: { padding: '28px 24px', textAlign: 'center', fontSize: '13.5px', color: 'var(--text-soft)' },
    captureLabel: { fontSize: '13px', color: 'var(--text-mid)', marginBottom: '12px' },
    captureDrop: { border: '1.5px dashed var(--gold)', borderRadius: '12px', background: 'var(--cream)', padding: '26px 16px', textAlign: 'center', cursor: 'pointer' },
    captureDropActive: { borderColor: 'var(--sage)', background: 'var(--mint)' },
    captureIcon: { fontSize: '22px', marginBottom: '6px' },
    captureTitle: { fontSize: '14px', fontWeight: '600', color: 'var(--text-dark)' },
    captureSub: { fontSize: '12.5px', color: 'var(--text-soft)', marginTop: '4px' },
    captureBrowse: { color: 'var(--gold)', textDecoration: 'underline' },
    captureOr: { display: 'flex', alignItems: 'center', margin: '14px 0', borderTop: '1px solid var(--border-light)', justifyContent: 'center', height: 0 },
    captureOrText: { background: 'white', padding: '0 10px', fontSize: '11.5px', color: 'var(--text-faint)' },
    captureLiveRow: { display: 'flex', alignItems: 'center', gap: '12px', border: '1px solid var(--border)', borderRadius: '10px', padding: '12px 16px', cursor: 'pointer', boxShadow: 'var(--shadow-sm)' },
    captureLiveDot: { width: '10px', height: '10px', borderRadius: '50%', background: 'var(--red)', flexShrink: 0 },
    captureLiveTitle: { fontSize: '14px', fontWeight: '600', color: 'var(--text-dark)' },
    captureLiveSub: { fontSize: '12px', color: 'var(--text-soft)' },
    captureFooter: { fontSize: '12px', color: 'var(--gold)', textAlign: 'center', marginTop: '12px' },
    captureNotesLink: { background: 'none', border: 'none', padding: 0, color: 'var(--text-soft)', fontSize: '12px', textDecoration: 'underline', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" },
    tabsWrap: { marginBottom: '24px', borderBottom: '1px solid var(--border)' },
    tabs: { display: 'flex', gap: '0', overflowX: 'auto' },
    tab: { padding: '10px 18px', border: 'none', background: 'none', fontFamily: "'DM Sans', sans-serif", fontSize: '13.5px', color: 'var(--text-soft)', cursor: 'pointer', whiteSpace: 'nowrap', borderBottom: '2px solid transparent', marginBottom: '-1px' },
    tabActive: { color: 'var(--sage)', borderBottomColor: 'var(--sage)', fontWeight: '600' },
    tabContent: {},
    grid: { display: 'grid', gridTemplateColumns: '1fr 340px', gap: '20px', alignItems: 'start' },
    mainCol: { display: 'flex', flexDirection: 'column', gap: '20px' },
    sideCol: { display: 'flex', flexDirection: 'column', gap: '20px' },
    card: { background: 'white', borderRadius: '14px', border: '1px solid var(--border)', overflow: 'hidden', boxShadow: 'var(--shadow-sm)', marginBottom: '20px' },
    cardHeader: { padding: '15px 22px', borderBottom: '1px solid var(--border-light)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
    cardTitle: { fontSize: '13.5px', fontWeight: '600', color: 'var(--text-dark)' },
    cardAction: { fontSize: '12.5px', color: 'var(--sage)', background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", fontWeight: '500' },
    cardBody: { padding: '16px 22px' },
    sessionCard: { background: 'white', borderRadius: '12px', border: '1px solid var(--border)', padding: '16px 20px', marginBottom: '12px', display: 'flex', alignItems: 'flex-start', gap: '16px', cursor: 'pointer', boxShadow: 'var(--shadow-sm)' },
    sessionDateBlock: { textAlign: 'center', minWidth: '40px' },
    sessionDay: { fontFamily: "'Lora', Georgia, serif", fontSize: '22px', color: 'var(--sage)', lineHeight: 1 },
    sessionMonth: { fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--text-faint)' },
    sessionYear: { fontSize: '9px', color: 'var(--text-faint)' },
    sessionInfo: { flex: 1, minWidth: 0 },
    sessionTitle: { fontSize: '14.5px', fontWeight: '500', color: 'var(--text-dark)', marginBottom: '4px' },
    sessionPreview: { fontSize: '12.5px', color: 'var(--text-soft)', marginBottom: '8px', lineHeight: 1.5 },
    sessionTags: { display: 'flex', gap: '6px', flexWrap: 'wrap' },
    themeTag: { background: 'var(--mint)', color: 'var(--sage)', padding: '3px 10px', borderRadius: '20px', fontSize: '11px' },
    goalTag: { background: '#FAF4EE', color: '#C4956A', padding: '3px 10px', borderRadius: '20px', fontSize: '11px' },
    breakthroughTag: { background: 'var(--gold-pale)', color: '#C9A84C', padding: '3px 10px', borderRadius: '20px', fontSize: '11px' },
    groupSessionTag: { background: 'rgba(196,149,106,0.15)', color: 'var(--gold)', border: '1px solid rgba(196,149,106,0.4)', padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '600' },
    groupSessionTagSmall: { background: 'rgba(196,149,106,0.12)', color: 'var(--gold)', border: '1px solid rgba(196,149,106,0.3)', padding: '2px 8px', borderRadius: '20px', fontSize: '10px', fontWeight: '600', marginRight: '6px' },
    sessionParticipants: { fontSize: '11.5px', color: 'var(--text-soft)', marginBottom: '6px', fontStyle: 'italic' },
    chevron: { color: 'var(--text-faint)', fontSize: '16px', marginTop: '4px' },
    breakthroughCard: { background: 'var(--gold-pale)', border: '1px solid rgba(201,168,76,0.2)', borderRadius: '10px', padding: '14px 16px', marginBottom: '12px' },
    breakthroughQuote: { fontSize: '14px', fontStyle: 'italic', color: 'var(--text-dark)', lineHeight: 1.6, marginBottom: '6px' },
    breakthroughContext: { fontSize: '12px', color: 'var(--text-soft)', marginBottom: '4px' },
    breakthroughSignificance: { fontSize: '12.5px', color: 'var(--text-mid)', marginBottom: '6px' },
    breakthroughMeta: { fontSize: '11px', color: 'var(--text-faint)' },
    themeGrid: { display: 'flex', flexWrap: 'wrap', gap: '8px' },
    themeItem: { display: 'flex', alignItems: 'center', gap: '6px', background: 'var(--mint)', borderRadius: '20px', padding: '5px 12px' },
    themeText: { fontSize: '12.5px', color: 'var(--text-dark)' },
    themeCount: { fontSize: '11px', color: 'var(--sage)', fontWeight: '600' },
    groupTag: { background: 'var(--mint)', color: 'var(--sage)', padding: '6px 14px', borderRadius: '20px', fontSize: '12.5px', cursor: 'pointer', fontWeight: '500' },
    goalRow: { display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 0', borderBottom: '1px solid var(--border-light)' },
    goalManageRow: { display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 0', borderBottom: '1px solid var(--border-light)', flexWrap: 'wrap' },
    goalText: { flex: 1, fontSize: '14px', color: 'var(--text-dark)' },
    goalStatusTag: { padding: '3px 10px', borderRadius: '20px', fontSize: '11.5px', border: '1px solid' },
    goalActions: { display: 'flex', gap: '6px', alignItems: 'center' },
    goalStatusBtn: { padding: '4px 10px', borderRadius: '20px', border: '1px solid var(--border)', background: 'none', color: 'var(--text-soft)', fontSize: '11.5px', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" },
    removeBtn: { background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-faint)', fontSize: '18px', padding: '0 4px' },
    addGoalRow: { display: 'flex', gap: '10px', alignItems: 'center', marginTop: '16px' },
    notesSection: { marginBottom: '16px' },
    notesLabel: { fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-soft)', marginBottom: '8px' },
    notesText: { fontSize: '14px', color: 'var(--text-mid)', lineHeight: 1.7 },
    sessionItemTitle: { fontSize: '16px', fontWeight: '500', color: 'var(--text-dark)', marginBottom: '4px' },
    sessionItemDate: { fontSize: '13px', color: 'var(--text-soft)', marginBottom: '12px' },
    sessionOverview: { fontSize: '14px', color: 'var(--text-mid)', lineHeight: 1.7, marginBottom: '12px' },
    tagRow: { display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' },
    linkBtn: { background: 'none', border: 'none', color: 'var(--sage)', fontSize: '13.5px', cursor: 'pointer', padding: 0, fontFamily: "'DM Sans', sans-serif", fontWeight: '500' },
    divider: { borderTop: '1px solid var(--border-light)', margin: '16px 0' },
    input: { width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--cream)', fontFamily: "'DM Sans', sans-serif", fontSize: '14px', color: 'var(--text-dark)', outline: 'none', boxSizing: 'border-box' },
    textarea: { width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--cream)', fontFamily: "'DM Sans', sans-serif", fontSize: '14px', color: 'var(--text-dark)', outline: 'none', boxSizing: 'border-box', resize: 'vertical' },
    saveBtn: { padding: '10px 18px', borderRadius: '8px', background: 'var(--sage)', border: 'none', color: 'white', fontFamily: "'DM Sans', sans-serif", fontSize: '13.5px', cursor: 'pointer', fontWeight: '500' },
    cancelBtn: { padding: '10px 18px', borderRadius: '8px', background: 'none', border: '1px solid var(--border)', color: 'var(--text-mid)', fontFamily: "'DM Sans', sans-serif", fontSize: '13.5px', cursor: 'pointer' },
    addGoalBtn: { padding: '10px 16px', borderRadius: '8px', background: 'var(--sage)', border: 'none', color: 'white', fontFamily: "'DM Sans', sans-serif", fontSize: '13.5px', cursor: 'pointer', whiteSpace: 'nowrap' },

    // Delete-client confirmation modal
    deleteModalOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' },
    deleteModal: { background: '#fff', borderRadius: '14px', padding: '32px', maxWidth: '420px', width: '100%', boxShadow: 'var(--shadow-lg)' },
    deleteModalTitle: { fontFamily: "'Lora', serif", fontSize: '20px', fontWeight: '600', color: 'var(--red)', margin: '0 0 12px' },
    deleteModalBody: { fontSize: '14px', color: 'var(--text-mid)', lineHeight: 1.6, margin: '0 0 20px' },
    deleteModalPrompt: { fontSize: '13px', color: 'var(--text-mid)', marginBottom: '8px' },
    deleteConfirmBtn: { padding: '11px 20px', borderRadius: '8px', background: 'var(--red)', border: 'none', color: '#fff', fontFamily: "'DM Sans', sans-serif", fontSize: '14px', fontWeight: '600', cursor: 'pointer', flex: 1 },
    accessCodeBox: { background: 'var(--mint)', borderRadius: '8px', padding: '12px 16px', fontSize: '13px', fontFamily: 'monospace', color: 'var(--text-dark)', wordBreak: 'break-all', marginTop: '8px' },
    messagesList: { display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '20px', maxHeight: '500px', overflowY: 'auto', padding: '8px 0' },
    messageRow: { display: 'flex', alignItems: 'flex-end', gap: '8px' },
    messageRowCoach: { flexDirection: 'row-reverse' },
    messageRowClient: { flexDirection: 'row' },
    messageAvatar: { width: '28px', height: '28px', borderRadius: '50%', background: 'var(--mint)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: '600', color: 'var(--sage)', flexShrink: 0 },
    messageMeta: { display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px', flexWrap: 'wrap' },
    messageSender: { fontSize: '11px', fontWeight: '600', color: 'var(--text-soft)', textTransform: 'uppercase', letterSpacing: '0.05em' },
    messageTime: { fontSize: '11px', color: 'var(--text-faint)' },
    includeBtn: { fontSize: '11px', padding: '1px 6px', borderRadius: '20px', border: '1px solid var(--border)', background: 'none', color: 'var(--text-faint)', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" },
    includeBtnActive: { borderColor: 'var(--sage)', color: 'var(--sage)', background: 'var(--mint)' },
    messageBubble: { borderRadius: '12px', padding: '10px 14px', fontSize: '14px', lineHeight: 1.6 },
    messageBubbleCoach: { background: 'var(--forest)', color: 'white', borderBottomRightRadius: '4px' },
    messageBubbleClient: { background: 'var(--mint)', color: 'var(--text-dark)', borderBottomLeftRadius: '4px' },
    messageInputArea: { borderTop: '1px solid var(--border-light)', paddingTop: '16px', marginTop: '8px' },
    messageInputRow: { display: 'flex', gap: '10px', alignItems: 'flex-end' },
    messageInput: { flex: 1, padding: '10px 14px', borderRadius: '10px', border: '1px solid var(--border)', background: 'var(--cream)', fontFamily: "'DM Sans', sans-serif", fontSize: '14px', color: 'var(--text-dark)', outline: 'none', resize: 'none' },
    messageInputActions: { display: 'flex', gap: '8px', alignItems: 'center' },
    voiceBtn: { padding: '10px 12px', borderRadius: '8px', background: 'none', border: '1px solid var(--border)', cursor: 'pointer', fontSize: '16px' },
    recordingBar: { display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', background: 'var(--red-light)', borderRadius: '10px' },
    recordingDot: { width: '10px', height: '10px', borderRadius: '50%', background: 'var(--red)', animation: 'pulse 1s ease-in-out infinite' },
    stopRecordBtn: { padding: '8px 16px', borderRadius: '8px', background: 'var(--red)', border: 'none', color: 'white', fontFamily: "'DM Sans', sans-serif", fontSize: '13px', cursor: 'pointer' },
    voicePreview: { background: 'var(--cream)', borderRadius: '10px', padding: '14px' },
    analysisCard: { background: 'var(--mint)', borderRadius: '10px', padding: '16px', marginBottom: '20px' },
    analysisSectionTitle: { fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--sage)', marginBottom: '8px' },
    analysisLabel: { fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--text-soft)', marginBottom: '4px' },
    analysisText: { fontSize: '14px', color: 'var(--text-dark)', lineHeight: 1.7 },
    emptyState: { textAlign: 'center', padding: '48px 24px', color: 'var(--text-soft)' },
    emptyIcon: { fontSize: '36px', marginBottom: '14px' },
    emptyTitle: { fontFamily: "'Lora', Georgia, serif", fontSize: '20px', fontWeight: '400', color: 'var(--text-dark)', marginBottom: '8px' },
    emptyText: { fontSize: '14px', color: 'var(--text-soft)', marginBottom: '16px' },
    emptyBtn: { padding: '10px 20px', borderRadius: '8px', background: 'var(--sage)', border: 'none', color: 'white', fontFamily: "'DM Sans', sans-serif", fontSize: '13.5px', cursor: 'pointer' },
    arcPrompt: { textAlign: 'center', padding: '28px 16px' },
    arcPromptIcon: { fontSize: '28px', color: 'var(--sage)', marginBottom: '12px' },
    arcPromptText: { fontSize: '14px', color: 'var(--text-mid)', lineHeight: 1.6, marginBottom: '20px', maxWidth: '440px', margin: '0 auto 20px' },
    arcBtn: { padding: '10px 22px', borderRadius: '8px', background: 'var(--sage)', border: 'none', color: 'white', fontFamily: "'DM Sans', sans-serif", fontSize: '14px', fontWeight: '500', cursor: 'pointer' },
    arcLoading: { textAlign: 'center', padding: '24px' },
    arcLoadingText: { fontSize: '14px', color: 'var(--text-soft)', marginBottom: '12px' },
    progressBar: { height: '3px', background: 'var(--border)', borderRadius: '2px', overflow: 'hidden', margin: '10px auto', maxWidth: '300px' },
    progressFill: { height: '100%', background: 'linear-gradient(90deg, var(--sage), var(--gold))', borderRadius: '2px', width: '100%', animation: 'pulse 1.5s ease-in-out infinite' },
    arcHeadline: { fontFamily: "'Lora', Georgia, serif", fontSize: '19px', fontWeight: '400', fontStyle: 'italic', color: 'var(--text-dark)', marginBottom: '20px', lineHeight: 1.4, paddingBottom: '16px', borderBottom: '1px solid var(--border-light)' },
    arcNarrative: { marginBottom: '24px' },
    arcParagraph: { fontSize: '14px', lineHeight: 1.8, color: 'var(--text-mid)', marginBottom: '14px' },
    arcSection: { marginBottom: '20px' },
    arcSectionTitle: { fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-soft)', marginBottom: '10px' },
    arcShift: { fontSize: '14px', color: 'var(--text-dark)', padding: '8px 12px', background: 'var(--mint)', borderRadius: '8px', marginBottom: '6px' },
    arcEdge: { fontSize: '14px', color: 'var(--text-dark)', padding: '8px 12px', background: '#FAF4EE', borderRadius: '8px', marginBottom: '6px' },
    arcInsights: { background: 'var(--cream)', borderRadius: '10px', padding: '16px', marginTop: '8px' },
    arcInsightsText: { fontSize: '14px', color: 'var(--text-mid)', lineHeight: 1.7, fontStyle: 'italic' },
    avatarSummary: { background: 'var(--mint)', borderRadius: '10px', padding: '16px 20px', marginBottom: '20px' },
    avatarSummaryLabel: { fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--sage)', marginBottom: '8px' },
    avatarSummaryText: { fontSize: '14px', color: 'var(--text-dark)', lineHeight: 1.7, fontStyle: 'italic' },
    avatarUpdated: { fontSize: '11px', color: 'var(--text-faint)', marginTop: '8px' },
    avatarSection: { padding: '16px 0', borderBottom: '1px solid var(--border-light)' },
    avatarSectionHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' },
    avatarSectionLabel: { fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-soft)' },
    avatarSectionText: { fontSize: '14px', color: 'var(--text-dark)', lineHeight: 1.7 },
    tagFilterRow: { display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', padding: '12px 0', borderBottom: '1px solid var(--border-light)', marginBottom: '16px' },
    tagFilterLabel: { fontSize: '11.5px', fontWeight: '600', color: 'var(--text-soft)', textTransform: 'uppercase', letterSpacing: '0.06em', marginRight: '4px' },
    tagFilterPill: { padding: '5px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" },
    taggedView: { marginBottom: '20px' },
    taggedViewTitle: { fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--text-soft)', marginBottom: '12px' },
    taggedEmpty: { fontSize: '13.5px', color: 'var(--text-faint)', fontStyle: 'italic', padding: '12px 0' },
    taggedMessage: { background: 'var(--cream)', borderRadius: '10px', padding: '14px 16px', marginBottom: '10px', border: '1px solid var(--border-light)' },
    taggedMessageMeta: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' },
    taggedMessageContent: { fontSize: '14px', color: 'var(--text-dark)', lineHeight: 1.6 },
    msgTagRow: { display: 'flex', gap: '4px', marginTop: '6px', flexWrap: 'wrap' },
    msgTagBtn: { padding: '3px 8px', borderRadius: '20px', fontSize: '11px', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", fontWeight: '500', transition: 'all 0.15s' },
};